/* Loads the Google Maps JS API exactly once, on demand.
 *
 * On demand because the map is one of five workspaces: a visitor who never
 * opens it should never pay for the API. The shell mounts workspaces lazily, so
 * nothing here runs until Map is first navigated to.
 *
 * Exactly once because the API installs itself on `window.google` and warns
 * (then ignores the second call) if bootstrapped twice — and `g-map` legitimately
 * re-initialises its Map object when the theme flips, so a second load attempt
 * is a normal event, not a bug.
 *
 * v1 pastes Google's minified bootstrap snippet inline (app/static/app.js:1016).
 * This is the same thing written out: script tag, callback, promise. The
 * `loading=async` + `callback` pair is the documented form for a non-blocking
 * load, and `libraries=maps,marker` is what makes `google.maps.marker`
 * (AdvancedMarkerElement) available without a further importLibrary round-trip.
 *
 * No DOM component knowledge and no Lit — this belongs in core/ despite touching
 * document.head, in the same way api.ts touches fetch.
 */

/** Resolves when `window.google.maps` is usable. Rejects if the script 404s. */
let pending: Promise<void> | null = null;

/**
 * `language=en` is deliberate, and inherited from v1: without it the basemap
 * renders place labels in the local script (Devanagari around Varanasi,
 * Balinese around Ubud), which is unreadable next to Latin-script venue pills.
 * Google's own map does the same thing in English — there is no supported
 * per-label transliteration option, so the whole basemap is pinned to English.
 */
const PARAMS = 'v=weekly&language=en&libraries=maps,marker&loading=async';

const CALLBACK = '__grainMapsReady';

/**
 * Load the API with a browser key. Repeat calls return the first promise,
 * including while it is still in flight — the key is read from `/api/me` and is
 * the same on every call.
 */
export function loadMaps(key: string): Promise<void> {
  if (pending) return pending;

  pending = new Promise<void>((resolve, reject) => {
    // The API calls a global by name, so the resolver has to be reachable from
    // window. Deleted on arrival rather than left behind as app state.
    (window as unknown as Record<string, unknown>)[CALLBACK] = () => {
      delete (window as unknown as Record<string, unknown>)[CALLBACK];
      resolve();
    };
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&${PARAMS}&callback=${CALLBACK}`;
    s.async = true;
    // A bad or unbilled key still loads the script and reports the failure on
    // the console instead — this only fires for a genuine network failure.
    s.onerror = () => {
      pending = null; // let a later visit retry rather than failing forever
      reject(new Error('Google Maps failed to load'));
    };
    document.head.append(s);
  });

  return pending;
}

/** Whether the API is already resident, for callers that can skip a spinner. */
export const mapsLoaded = (): boolean =>
  typeof google !== 'undefined' && !!google.maps?.Map;
